

import { Link } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import { useSellerOptionsQuery } from "@/modules/stores/services/queries/useSellerOptionsQuery";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { useStoresQuery } from "@/modules/stores/services/queries/useStoresQuery";
import { StoreFilters } from "@/modules/stores/components/StoreFilters";
import { ImpersonateButton } from "@/shared/components/ImpersonateButton";
import { ShareLinkButton } from "@/shared/components/ShareLinkButton";
import {
  getBuyerAbsoluteUrl,
  getSellerAbsoluteUrl,
} from "@/lib/routing/portal-urls";
import { EmptyState, LoadingSkeleton, ErrorState } from "@a2z/ui";

type StoresPageProps = {
  locale: string;
  limit: number;
  offset: number;
  sellerId?: string;
};

function buildQuery(params: {
  limit: number;
  offset: number;
  sellerId?: string;
}) {
  const query = new URLSearchParams();
  query.set("limit", String(params.limit));
  query.set("offset", String(params.offset));
  if (params.sellerId) {
    query.set("sellerId", params.sellerId);
  }
  return query.toString();
}

export function StoresPage({
  locale,
  limit,
  offset,
  sellerId,
}: StoresPageProps) {
  const t = useTranslations();
  const { data: storesData, isLoading, error, refetch } = useStoresQuery({
    limit,
    offset,
    sellerId,
  });
  const { data: sellersData } = useSellerOptionsQuery();
  const { data: session } = useSessionQuery();

  const impersonationRoles = [
    ADMIN_ROLES.ADMIN,
    ADMIN_ROLES.SUPER_ADMIN,
    ADMIN_ROLES.CUSTOMER_SERVICE,
  ];
  const canImpersonateStore =
    session?.admin?.roles?.some((role) =>
      impersonationRoles.includes(role as (typeof impersonationRoles)[number]),
    ) ?? false;

  if (isLoading) {
    return <LoadingSkeleton variant="list" rows={5} />;
  }

  if (error || !storesData) {
    return <ErrorState onRetry={refetch} />;
  }

  const hasPrev = offset > 0;
  const hasNext = offset + limit < storesData.total;
  const prevOffset = Math.max(offset - limit, 0);
  const nextOffset = offset + limit;

  return (
    <section className="flex w-full max-w-4xl flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[23px] font-bold leading-tight tracking-[-0.025em] lg:text-[28px] lg:font-semibold lg:tracking-[-0.01em] text-[color:var(--text-primary)]">
            {t(keys.admin.storesTitle)}
          </h1>
        </div>
        <StoreFilters
          basePath={`/${locale}/admin/stores`}
          limit={limit}
          initialSellerId={sellerId}
          sellerOptions={sellersData?.items ?? []}
        />
      </div>

      {storesData.items.length === 0 ? (
        <EmptyState
          title={t(keys.common.emptyTitle)}
          description={t(keys.admin.storesEmptySubtitle)}
        />
      ) : (
        <div className="grid gap-3">
          {storesData.items.map((store) => (
            <div
              key={store.id}
              className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 text-sm transition hover:bg-[color:var(--hover)] sm:py-2.5"
            >
              <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center sm:gap-4">
                <Link
                  to={`/${locale}/admin/stores/${store.id}`}
                  className="grid gap-2 sm:grid-cols-3 sm:items-center sm:gap-4"
                >
                  <div>
                    <div className="text-sm font-semibold text-[color:var(--text-primary)]">
                      {store.storeName}
                    </div>
                    <div className="text-xs text-[color:var(--text-muted)] sm:hidden">
                      {store.storeSlug}
                    </div>
                  </div>
                  <div className="hidden text-xs text-[color:var(--text-muted)] sm:block">
                    {store.storeSlug}
                  </div>
                  <div className="text-xs text-[color:var(--text-muted)]">
                    {t(keys.common.seller)}: {store.seller.name}
                  </div>
                </Link>
                <div className="flex flex-col items-start gap-2 sm:items-end">
                  {canImpersonateStore ? (
                    <ImpersonateButton
                      storeId={store.id}
                      locale={locale}
                      adminRoles={session?.admin?.roles}
                      className="h-9 rounded-full px-3 text-xs font-semibold"
                    />
                  ) : null}
                  <ShareLinkButton
                    path={getSellerAbsoluteUrl(locale, store.storeSlug)}
                    label={t(keys.admin.storeListSellerDashboardLabel)}
                    copiedLabel={t(keys.admin.linkCopiedTooltip)}
                    ariaLabel={t(keys.admin.shareSellerDashboardTooltip)}
                    size="sm"
                  />
                  <ShareLinkButton
                    path={getBuyerAbsoluteUrl(locale, store.storeSlug)}
                    label={t(keys.admin.storeListCustomerProductsLabel)}
                    copiedLabel={t(keys.admin.linkCopiedTooltip)}
                    ariaLabel={t(keys.admin.shareStorefrontTooltip)}
                    size="sm"
                  />
                </div>
              </div>
              <a
                href={getBuyerAbsoluteUrl(locale, store.storeSlug)}
                target="_blank"
                rel="noreferrer"
                dir="ltr"
                className="mt-2 block truncate text-xs text-[color:var(--text-secondary)] underline underline-offset-2 hover:text-[color:var(--text-primary)]"
              >
                {getBuyerAbsoluteUrl(locale, store.storeSlug).replace(
                  /^https?:\/\//,
                  "",
                )}
              </a>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between text-sm">
        {hasPrev ? (
          <Link
            to={`/${locale}/admin/stores?${buildQuery({
              limit,
              offset: prevOffset,
              sellerId,
            })}`}
            className="text-[color:var(--text-secondary)]"
          >
            {t(keys.common.previous)}
          </Link>
        ) : (
          <span className="text-[color:var(--text-disabled)]">
            {t(keys.common.previous)}
          </span>
        )}
        {hasNext ? (
          <Link
            to={`/${locale}/admin/stores?${buildQuery({
              limit,
              offset: nextOffset,
              sellerId,
            })}`}
            className="text-[color:var(--text-secondary)]"
          >
            {t(keys.common.next)}
          </Link>
        ) : (
          <span className="text-[color:var(--text-disabled)]">
            {t(keys.common.next)}
          </span>
        )}
      </div>
    </section>
  );
}
